import React, {Component} from "react"
import { clamp } from "../../services/utils/math-util"
import { getElementLayoutInfo } from "../../services/utils/html-util"
import "./index.css"

interface Props {
    title?: string
    draggable?: boolean
    maximized?: boolean
    customClass?: string
    sizes?: string[]
    onClose?: () => void
    showMaximizeButton?: boolean
    showHelpButton?: boolean
    resizable?: boolean
    onHelp?: () => void
    onDragging?: (e: MouseEvent) => void
    onDraggingEnd?: () => void
}

interface State {
    maximized: boolean
}

/**
 * This is a generic draggable/movable window frame that can be used as a container
 */
export default class DraggableWindow extends Component<Props, State> {
    containerRef

    constructor(props) {
        super(props)
        this.containerRef = React.createRef()
        this.state = {
            maximized: false,
        }
    }

    componentDidMount() {
        let {draggable, maximized} = this.props

        if (draggable) {
            this.enableDragging()
        }

        if (maximized) {
            this.setState({maximized})
        }

        window.addEventListener("resize", this.handleWindowResize)
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleWindowResize)
    }

    handleWindowResize = () => {
        let container = this.containerRef.current
        let layoutContext = getElementLayoutInfo(container)

        if (layoutContext && layoutContext.parent) {
            let {width, height} = layoutContext.parent.rect
            container.style.left = `${width/2}px`
            container.style.top = `${height/2}px`
        }
    }

    enableDragging = () => {
        let {onDragging, onDraggingEnd} = this.props
        let container = this.containerRef.current
        let innerContainer = container?.querySelector(".draggable-window-top-panel") as HTMLElement

        if (!container || !innerContainer) return

        let offsetX = 0
        let offsetY = 0
        let dragging = false

        let onMouseDown = (e: MouseEvent) => {
            dragging = true
            offsetX = e.clientX - container.offsetLeft
            offsetY = e.clientY - container.offsetTop
            document.addEventListener("mousemove", onMouseMove)
            document.addEventListener("mouseup", onMouseUp)
        }

        let onMouseMove = (e: MouseEvent) => {
            if (dragging) {
                if (onDragging) {
                    onDragging(e)
                }

                let {clientX, clientY} = e
                let layoutContext = getElementLayoutInfo(container)

                if (layoutContext?.parent) {
                    let {rect: parentRect} = layoutContext.parent
                    let padding = 30
                    let boundaries = { minX: padding, maxX: parentRect.width-padding, minY: padding, maxY: parentRect.height-padding}

                    // Enforce the boundaries!
                    clientX = clamp(clientX, boundaries.minX, boundaries.maxX)
                    clientY = clamp(clientY, boundaries.minY, boundaries.maxY)
                }

                let left = clientX - offsetX
                let top = clientY - offsetY

                container.style.left = `${left}px`
                container.style.top = `${top}px`
            }
        }

        let onMouseUp = () => {
            dragging = false

            if (onDraggingEnd) {
                onDraggingEnd()
            }

            document.removeEventListener("mousemove", onMouseMove)
            document.removeEventListener("mouseup", onMouseUp)
        }

        innerContainer.addEventListener("mousedown", onMouseDown)
    }

    handleClose = (e) => {
        e.preventDefault()
        let {onClose} = this.props
        if (onClose) {
            onClose()
        }
    }

    toggleMaximize = (e) => {
        e.preventDefault()
        let {maximized} = this.state
        this.setState({maximized: !maximized})
    }

    showHelp = (e) => {
        e.preventDefault()
        let {onHelp} = this.props
        if (onHelp) {
            onHelp()
        }
    }

    render() {
        let {title="Draggable Window", customClass="", sizes, children, showMaximizeButton=true, showHelpButton=false, resizable=false} = this.props
        let {maximized} = this.state
        let sizeClass = resizable ? "": sizes && `${maximized ? sizes[1]: sizes[0]} ` || `${maximized ? "wider": ""}`
        let leftClass = sizes && `${maximized ? "left0": "left50"}` || ""
        let resizableClass = `${resizable ? "resizable-container" : ""}`
        let containerClass = `draggable-window-container ${sizeClass} ${leftClass} ${customClass} ${resizableClass}`

        return (
            <div ref={this.containerRef} className={containerClass}>
                <div className={`draggable-window-top-panel`}>
                    <div className="draggable-window-title">
                        {title}
                    </div>

                    <div className="draggable-window-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="12" cy="9" r="1"/>
                                <circle cx="19" cy="9" r="1"/>
                                <circle cx="5" cy="9" r="1"/>
                                <circle cx="12" cy="15" r="1"/>
                                <circle cx="19" cy="15" r="1"/>
                                <circle cx="5" cy="15" r="1"/>
                        </svg>
                    </div>

                    <div className="draggable-window-controls">
                        {showMaximizeButton &&
                            <button className="simple-btn" aria-label="Toggle Maximize window" onClick={this.toggleMaximize}>
                                {!maximized ?
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" 
                                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" >
                                        <path d="M15 3h6v6"/>
                                        <path d="m21 3-7 7"/><path d="m3 21 7-7"/>
                                        <path d="M9 21H3v-6"/>
                                    </svg>
                                    :
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" 
                                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="m14 10 7-7"/><path d="M20 10h-6V4"/>
                                        <path d="m3 21 7-7"/>
                                        <path d="M4 14h6v6"/>
                                    </svg>
                                }
                            </button>
                        }
                        {showHelpButton &&
                            <button className="simple-btn" aria-label="Get Help" onClick={this.showHelp}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" 
                                    className="lucide lucide-circle-question-mark-icon lucide-circle-question-mark"><circle cx="12" cy="12" r="10"/>
                                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                                    <path d="M12 17h.01"/>
                                </svg>
                            </button>
                        }
                        <button className="simple-btn" aria-label="Close" onClick={this.handleClose}>
                            <svg width="24" height="24" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path 
                                d="M7.4529 7.24138C7.52734 7.3121 7.58686 7.397 7.62797 7.49109C7.66908 7.58518 7.69093 7.68654 7.69224 7.7892C7.69356 7.89187 7.67431 7.99376 7.63562 8.08887C7.59694 8.18398 7.53961 8.27038 7.467 8.34298C7.3944 8.41558 7.308 8.47292 7.21289 8.5116C7.11778 8.55028 7.01589 8.56953 6.91323 8.56822C6.81056 8.56691 6.7092 8.54505 6.61511 8.50395C6.52103 8.46284 6.43612 8.40331 6.3654 8.32888L3.83223 5.79619L1.29905 8.32888C1.15362 8.46705 0.959969 8.54294 0.759383 8.54037C0.558797 8.5378 0.36715 8.45698 0.225302 8.31513C0.0834552 8.17328 0.00263125 7.98164 6.31164e-05 7.78105C-0.00250502 7.58046 0.073386 7.38681 0.211555 7.24138L2.74425 4.7082L0.211555 2.17503C0.073386 2.0296 -0.00250502 1.83595 6.31164e-05 1.63536C0.00263125 1.43477 0.0834552 1.24313 0.225302 1.10128C0.36715 0.959432 0.558797 0.878608 0.759383 0.87604C0.959969 0.873472 1.15362 0.949363 1.29905 1.08753L3.83223 3.62022L6.3654 1.08753C6.51083 0.949363 6.70449 0.873472 6.90507 0.87604C7.10566 0.878608 7.29731 0.959432 7.43915 1.10128C7.581 1.24313 7.66183 1.43477 7.66439 1.63536C7.66696 1.83595 7.59107 2.0296 7.4529 2.17503L4.92021 4.7082L7.4529 7.24138Z"
                                fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="draggable-window-content">
                    {children}
                </div>
            </div>
        )
    }
}

